import { BASE_API_AUTH } from 'utils/constants';
import axios, { AxiosError } from 'axios';
import { clearUserSession, getAccessToken, getRefreshToken, getUserSession, setUserSession } from './localStorageHelper';
import { removeAuthHeader, setAuthHeader } from './common';
import { uiPresenter, uiStore } from 'stores/root_store';

import queryString from 'query-string';

const SESSION_EXPIRED_MESSAGE = 'Phiên làm việc đã hết hạn, vui lòng đăng nhập lại.';
const SESSION_EXPIRED_STORAGE_KEY = 'session_expired_message';
let refreshTokenRequest: Promise<string> | null = null;
let isRedirectingToLogin = false;

const markSessionExpiredError = (error: unknown) => {
  if (typeof error === 'object' && error !== null) {
    Object.assign(error, { isSessionExpired: true });
    return error;
  }
  return { error, isSessionExpired: true };
};

const isSessionExpiredError = (error: any) => error?.isSessionExpired;

const handleSessionExpired = () => {
  sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, SESSION_EXPIRED_MESSAGE);
  clearUserSession();

  if (window.location.pathname === '/login') {
    uiPresenter.setMessageRefreshError(uiStore, SESSION_EXPIRED_MESSAGE);
    uiPresenter.setOpenModalAuthorize(uiStore, true);
    return;
  }

  if (!isRedirectingToLogin) {
    isRedirectingToLogin = true;
    window.location.replace('/login');
  }
};

const isRefreshTokenUrl = (url?: string) => !!url && url.includes('/auth/refresh-token');

const controller = new AbortController();
const axiosClient = axios.create({
  // baseURL: BASE_API_AUTH,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: (params) => queryString.stringify(params),
  signal: controller.signal
});

const refreshAccessToken = async () => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = axiosClient
      .post(`${BASE_API_AUTH}/auth/refresh-token`, undefined, {
        params: {
          refreshToken: getRefreshToken(),
        },
      })
      .then(({ data }) => {
        const userSession = getUserSession();
        const result = data?.data || {};
        const accessToken = result.access_token || result.accessToken?.token || result.accessToken || result.token;
        const refreshToken = result.refresh_token || result.accessToken?.refreshToken || result.refreshToken || getRefreshToken();
        const expiredTime = result.expireTime || result.expiredTime || result.accessToken?.expireTime || result.accessToken?.expiredTime;

        if (!accessToken) {
          throw new Error(SESSION_EXPIRED_MESSAGE);
        }

        Object.assign(userSession, {
          refreshToken,
          token: accessToken,
          expiredTime,
        });

        setUserSession(userSession);
        return accessToken;
      })
      .finally(() => {
        refreshTokenRequest = null;
      });
  }

  return refreshTokenRequest;
};

axiosClient.interceptors.request.use(async (config) => {
  const token = getAccessToken();

  if (token) {
    Object.assign(config.headers ?? {}, {
      Authorization: `Bearer ${token}`,
    });
  } else if (config.headers) {
    delete config.headers.Authorization;
  }

  return config;
});

axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const { response, config } = error;
    const status = response?.status;
    const originalRequest = config as any;

    if (status !== 401) return Promise.reject(error);

    if (!originalRequest || isRefreshTokenUrl(originalRequest.url) || originalRequest._retry) {
      handleSessionExpired();
      return Promise.reject(markSessionExpiredError(error));
    }

    originalRequest._retry = true;

    try {
      const accessToken = await refreshAccessToken();
      return axiosClient(
        Object.assign(originalRequest, {
          headers: {
            ...originalRequest.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        }),
      );
    } catch (refreshError) {
      handleSessionExpired();
      return Promise.reject(markSessionExpiredError(refreshError));
    }
  },
);

const shouldBypassResponseError = (error: any) => isSessionExpiredError(error);

// Method GET
export const get = async (
  baseApi: string,
  url: string,
  params?: object,
  shouldSetAuthHeader = true,
  shouldRemoveAuthHeader = false,
) => {
  if (shouldSetAuthHeader) {
    setAuthHeader();
  }
  if (shouldRemoveAuthHeader) {
    removeAuthHeader();
  }

  return await axiosClient
    .get('/' + url, {
      baseURL: baseApi,
      params: {
        ...params,
      },
    })
    .catch((e) => {
      if (shouldBypassResponseError(e)) {
        return Promise.reject(e);
      }
      if (e.response) {
        if (e.response.status === 500) {
          // history.push('/500');
          // window.location.reload();
          return e.response;
        }
        return e.response;
      }
    });
};




// Method DELETE
export const deleted = async (
  baseApi: string,
  url: string,
  params?: object,
  shouldSetAuthHeader = true,
  shouldRemoveAuthHeader = false,
) => {
  if (shouldSetAuthHeader) {
    setAuthHeader();
  }
  if (shouldRemoveAuthHeader) {
    removeAuthHeader();
  }

  return await axiosClient.delete('/' + url, {
    baseURL: baseApi,
    params: {
      ...params,
    },
  }).catch(async (e) => {
    if (shouldBypassResponseError(e)) {
      return Promise.reject(e);
    }
    if (e.response.status !== 200) {
      // message.error(`${e.response.data.message}`, 3);
      return e.response
    }
    return e.response;
  });;
};

// download file (PUT)
export const downloadFile = async (
  url: string,
  // params : string,
  shouldSetAuthHeader = true,
  shouldRemoveAuthHeader = false,
) => {
  if (shouldSetAuthHeader) {
    setAuthHeader();
  }
  if (shouldRemoveAuthHeader) {
    removeAuthHeader();
  }
  await axiosClient({
    url: BASE_API_AUTH + '/' + url,
    method: 'GET',
    responseType: 'blob',
  }).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const fileName = response.headers['content-disposition'].split(';')[1].replace('filename=', '').trim();
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); //or any other extension
    document.body.appendChild(link);
    link.click();
  }).catch((e) => {
    if (shouldBypassResponseError(e)) {
      return Promise.reject(e);
    }
  });
};

// Method POST
export async function post<T = unknown>(
  baseApi: string,
  url: string,
  params: T,
  shouldSetAuthHeader = true,
  shouldRemoveAuthHeader = false,
) {
  if (shouldSetAuthHeader) {
    setAuthHeader();
  }
  if (shouldRemoveAuthHeader) {
    removeAuthHeader();
  }

  // axios.defaults.headers.post['Content-Type'] = 'multipart/form-data';
  return await axiosClient
    .post('/' + url, params, {
      baseURL: baseApi,
    })
    .catch(async (e) => {
      if (shouldBypassResponseError(e)) {
        return Promise.reject(e);
      }
      if (e.response) {
        // if (e.response.status !== 200) {
        //   // message.error(`${e.response.data.message}`, 3);
        //   return e.response.data
        // }
        return e.response
      }
      console.log(e);

    });
}
// download file (POST)
export async function downloadFilePost<T = unknown>(
  baseApi: string,
  url: string,
  params: T,
  shouldSetAuthHeader = true,
  shouldRemoveAuthHeader = false,
) {
  if (shouldSetAuthHeader) {
    setAuthHeader();
  }
  if (shouldRemoveAuthHeader) {
    removeAuthHeader();
  }
  await axiosClient({
    url: '/' + url,
    method: 'POST',
    responseType: 'blob',
    data: params,
    baseURL: baseApi,
  })

    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const fileName = response.headers['content-disposition'].split(';')[1].replace('filename=', '').trim();
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName); //or any other extension
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }).catch((e) => {
      if (shouldBypassResponseError(e)) {
        return Promise.reject(e);
      }
      if (e.response) {
        if (e.response.status !== 200) {
          alert('Status: ' + e.response.status + '\n' + e.message);
        }
        return e;
      }
    });
}

// Method PUT
export async function put<T = unknown>(
  baseApi: string,
  url: string,
  params: T,
  shouldSetAuthHeader = true,
  shouldRemoveAuthHeader = false,
) {
  if (shouldSetAuthHeader) {
    setAuthHeader();
  }
  if (shouldRemoveAuthHeader) {
    removeAuthHeader();
  }
  return await axiosClient.put(baseApi + '/' + url, params).catch((e) => {
    if (shouldBypassResponseError(e)) {
      return Promise.reject(e);
    }
    if (e.response) {
      if (e.response.status !== 200) {
        /*
        Tạm thời chưa biết cách fix lỗi toast hiển thị một lần nên dùng message thay thế
        */
        // message.error(`${e.response.data.message}`, 5);
        // uiPresenter.setMessageSystemError(uiStore, true, e.response.status, e.response.data.message);
        // alert('Status: ' + e.response.status + '\n' + e.message + '\n' + e.response.data.message);
      }
      return e.response;
    }
  });
}
